import { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useRevit } from '../../contexts/RevitContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus, faSpinner, faTimes, faStar } from '@fortawesome/free-solid-svg-icons';
import './AccountManagement.css';

const AccountManagement = ({ isVIPAccount }) => {
  const { username, userEmail, subAccounts, hasSubAccounts } = useRevit();
  const SERVER_URL = process.env.REACT_APP_SERVER_URL;
  const [newAccountName, setNewAccountName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [accountToCancel, setAccountToCancel] = useState(null);
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const [accountToRemove, setAccountToRemove] = useState(null);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [accountToRename, setAccountToRename] = useState(null);
  const [newName, setNewName] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loadingMessage, setLoadingMessage] = useState('');
  const [showResubscribeModal, setShowResubscribeModal] = useState(false);
  const [accountToResubscribe, setAccountToResubscribe] = useState(null);
  const [resubscribeBillingCycle, setResubscribeBillingCycle] = useState('monthly');
  const { getToken } = useAuth();
  
  const createAccountCheckoutSession = async (planType) => {
    if (!newAccountName.trim()) {
      alert('Please enter an account name');
      return;
    }
    
    // Adjust plan type based on billing cycle
    const checkoutPlanType = planType === 'vip' ? 'vip' : billingCycle === 'annual' ? 
      `annual${planType.charAt(0).toUpperCase() + planType.slice(1)}` : 
      `monthly${planType.charAt(0).toUpperCase() + planType.slice(1)}`;
    
    setIsSubmitting(true);
    setLoadingMessage(`Creating ${billingCycle === 'annual' ? 'annual' : 'monthly'} ${planType} account...`);
    
    try {
      const token = await getToken();
      const response = await fetch(`https://${SERVER_URL}/api/create-account-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          userId: username, 
          planType: checkoutPlanType, 
          email: userEmail,
          accountName: newAccountName
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const { url } = await response.json();
      setLoadingMessage('Redirecting to checkout...');
      window.location.href = url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Failed to create checkout session. Please try again.');
      setIsSubmitting(false);
      setLoadingMessage('');
    }
  };

  const handleAddAccount = () => {
    if (isVIPAccount) {
      createAccountCheckoutSession('vip');
    } else {
      createAccountCheckoutSession('essential');
    }
  };
  
  const handleAddProAccount = () => {
    if (isVIPAccount) {
      createAccountCheckoutSession('vip');
    } else {
      createAccountCheckoutSession('pro');
    }
  };
  
  const toggleBillingCycle = () => {
    setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly');
  };
  
  const toggleResubscribeBillingCycle = () => {
    setResubscribeBillingCycle(resubscribeBillingCycle === 'monthly' ? 'annual' : 'monthly');
  };
  
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  const handleCancelAccount = (accountId) => {
    // Find the account to cancel
    const account = subAccounts.find(acc => acc.id === accountId);
    setAccountToCancel(account);
    setShowCancelConfirmation(true);
  };
  
  const confirmCancelAccount = async () => {
    if (!accountToCancel) return;
    const token = await getToken();
    
    setIsSubmitting(true);
    setLoadingMessage(`Cancelling subscription for ${accountToCancel.name}...`);
    
    try {
      const response = await fetch(`https://${SERVER_URL}/api/cancel-sub-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          userId: username,
          subAccountId: accountToCancel.id
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to cancel sub-account');
      }

      // Update the UI to reflect the cancellation
      setLoadingMessage('Refreshing account information...');
      window.location.reload();
    } catch (error) {
      console.error('Error cancelling sub-account:', error);
      alert('Failed to cancel sub-account. Please try again.');
      setIsSubmitting(false);
      setLoadingMessage('');
      setShowCancelConfirmation(false);
      setAccountToCancel(null);
    }
  };
  
  const handleRemoveAccount = (accountId) => {
    // Find the account to remove
    const account = subAccounts.find(acc => acc.id === accountId);
    setAccountToRemove(account);
    setShowRemoveConfirmation(true);
  };
  
  const confirmRemoveAccount = async () => {
    if (!accountToRemove) return;
    const token = await getToken();
    
    setIsSubmitting(true);
    setLoadingMessage(`Removing ${accountToRemove.name} from your account list...`);
    
    try {
      const response = await fetch(`https://${SERVER_URL}/api/remove-sub-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          userId: username,
          subAccountId: accountToRemove.id
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to remove sub-account');
      }

      // Update the UI to reflect the removal
      setLoadingMessage('Refreshing account information...');
      window.location.reload();
    } catch (error) {
      console.error('Error removing sub-account:', error);
      alert('Failed to remove sub-account. Please try again.');
      setIsSubmitting(false);
      setLoadingMessage('');
      setShowRemoveConfirmation(false);
      setAccountToRemove(null);
    }
  };
  
  const handleRenameAccount = (accountId, currentName) => {
    const account = subAccounts.find(acc => acc.id === accountId);
    setAccountToRename(account);
    setNewName(currentName);
    setShowRenameModal(true);
  };
  
  const confirmRenameAccount = async () => {
    if (!accountToRename || !newName.trim()) return;
    const token = await getToken();
    
    setIsSubmitting(true);
    setLoadingMessage(`Renaming account to ${newName}...`);
    
    try {
      const response = await fetch(`https://${SERVER_URL}/api/rename-sub-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          userId: username,
          subAccountId: accountToRename.id,
          newName: newName.trim()
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to rename sub-account');
      }

      // Update the UI to reflect the rename
      setLoadingMessage('Refreshing account information...');
      window.location.reload();
    } catch (error) {
      console.error('Error renaming sub-account:', error);
      alert('Failed to rename sub-account. Please try again.');
      setIsSubmitting(false);
      setLoadingMessage('');
      setShowRenameModal(false);
      setAccountToRename(null);
      setNewName('');
    }
  };

  const handleResubscribeAccount = (accountUsername, accountName) => {
    // For accounts with expiring date (active but set to cancel), directly resubscribe
    const account = subAccounts.find(acc => acc.username === accountUsername);
    
    if (account && account.expiring && account.active) {
      // Direct resubscription for accounts that are active but set to expire
      processResubscription(accountUsername, accountName, 'monthlyEssential');
    } else {
      // For inactive accounts, show the plan selection modal
      setAccountToResubscribe({
        username: accountUsername,
        name: accountName
      });
      setResubscribeBillingCycle('monthly'); // Reset to monthly by default
      setShowResubscribeModal(true);
    }
  };
  
  const processResubscription = async (accountUsername, accountName, planType) => {
    setIsSubmitting(true);
    setLoadingMessage(`Preparing to resubscribe ${accountName}...`);
    setShowResubscribeModal(false);
    
    try {
      const token = await getToken();
      const response = await fetch(`https://${SERVER_URL}/api/resubscribe-sub-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          subAccountUsername: accountUsername,
          subAccountName: accountName,
          planType: planType
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create resubscription checkout session');
      }

      const { url } = await response.json();
      window.location.href = url;
    } catch (error) {
      console.error('Error creating resubscription checkout session:', error);
      alert('Failed to create resubscription checkout session. Please try again.');
      setIsSubmitting(false);
      setLoadingMessage('');
    }
  };
  
  const handleResubscribeEssential = () => {
    if (!accountToResubscribe) return;
    
    const planType = resubscribeBillingCycle === 'annual' ? 'annualEssential' : 'monthlyEssential';
    processResubscription(
      accountToResubscribe.username,
      accountToResubscribe.name,
      planType
    );
  };
  
  const handleResubscribePro = () => {
    if (!accountToResubscribe) return;
    
    const planType = resubscribeBillingCycle === 'annual' ? 'annualPro' : 'monthlyPro';
    processResubscription(
      accountToResubscribe.username,
      accountToResubscribe.name,
      planType
    );
  };
  
  return (
    <div className="account-management-container">
      {isSubmitting && (
        <div className="loading-resub-overlay">
          <div className="loading-resubcontent">
            <FontAwesomeIcon icon={faSpinner} spin size="3x" />
            <div className="loading-resub-text">{loadingMessage}</div>
          </div>
        </div>
      )}
      
      <h1>Sub Accounts</h1>
      
      <div className="account-card">
        <h2>Connect an additional RevTrack account</h2>
        
        <p className="account-description">
          This is intended for:
        </p>
        <ul className="account-list">
          <li>Agency owners who want to separate data from multiple clients</li>
          <li>Youtubers with multiple channels who want data from different channels to be separated</li>
        </ul>
        
        <div className="form-group-accounts">
          <label htmlFor="new-account-name">Account Name:</label>
          <input
            id="new-account-name"
            type="text"
            value={newAccountName}
            onChange={(e) => setNewAccountName(e.target.value)}
            placeholder="Enter account name"
            required
          />
        </div>
        {!isVIPAccount && (
          <div className="billing-toggle">
            <span className={billingCycle === 'monthly' ? 'active' : ''}>Monthly</span>
            <label className="switch">
              <input 
                type="checkbox" 
                checked={billingCycle === 'annual'} 
                onChange={toggleBillingCycle}
              />
              <span className="slider round"></span>
            </label>
            <span className={billingCycle === 'annual' ? 'active' : ''}>Annual (2 months free)</span>
          </div>
        )}
        
        {/* Only show add account buttons if not VIP or VIP with less than 3 accounts */}
        {(!isVIPAccount || (isVIPAccount && subAccounts.length < 3)) && (
          <div className="account-actions">
            <button 
              className="btn-essential" 
              onClick={handleAddAccount}
            >
              <FontAwesomeIcon icon={faUserPlus} />
              {isVIPAccount ? 'Add Account ($2,400 - 20% off)' : `Add Essential Account (${billingCycle === 'annual' ? '$490/yr' : '$49/mo'})`}
            </button>
            {!isVIPAccount && (
              <button 
                className="btn-pro" 
                onClick={handleAddProAccount}
              >
                <FontAwesomeIcon icon={faUserPlus} />
                Add Pro Account ({billingCycle === 'annual' ? '$990/yr' : '$99/mo'})
              </button>
            )}
          </div>
        )}
        
      </div>
      
      <div className="account-card">
        <h2>Your Sub Accounts</h2>
        {hasSubAccounts && <p className="account-description">To switch active sub account, use the account selector in the top right of the page.</p>}
        {!hasSubAccounts ? (
          <p className="no-accounts">You don't have any connected accounts yet.</p>
        ) : subAccounts.length === 0 ? (
          <div className="loading-state">
            <FontAwesomeIcon icon={faSpinner} spin />
            <p>Loading your accounts...</p>
          </div>
        ) : (
          <div className="accounts-table-container">
            <table className="accounts-table">
              <thead>
                <tr>
                  <th className="account-name-col">Account Name</th>
                  <th className="account-status-col">Status</th>
                  <th className="account-actions-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {subAccounts.map(account => (
                  <tr key={account.id}>
                    <td className="account-name-col">{account.name}</td>
                    <td className="account-status-col">
                      {account.expiring ? (
                        <div className="status-expiring">
                          <span className="status-badge status-expiring">
                            Expires on {formatDate(account.expiring)}
                          </span>
                        </div>
                      ) : (
                        <span className={`status-badge-accounts ${account.active ? 'status-active' : 'status-inactive'}`}>
                          {account.active ? 'Active' : 'Inactive'}
                        </span>
                      )}
                    </td>
                    <td className="account-actions-col">
                      <div className="account-actions-buttons">
                        <button 
                          className="btn-rename-account"
                          onClick={() => handleRenameAccount(account.id, account.name)}
                        >
                          Rename
                        </button>
                        
                        {account.expiring && account.active ? (
                          // Show resubscribe button for accounts with expiring date
                          <button 
                            className="btn-resubscribe-account"
                            onClick={() => handleResubscribeAccount(account.username, account.name)}
                          >
                            Resubscribe
                          </button>
                        ) : account.active ? (
                          // Show cancel button for active accounts without expiring date
                          <button 
                            className="btn-cancel-account"
                            onClick={() => handleCancelAccount(account.id)}
                          >
                            Cancel
                          </button>
                        ) : (
                          // For inactive accounts without expiring date, show both resubscribe and remove
                          <div className="multiple-actions">
                            <button 
                              className="btn-resubscribe-account"
                              onClick={() => handleResubscribeAccount(account.username, account.name)}
                            >
                              Resubscribe
                            </button>
                            <button 
                              className="btn-remove-account"
                              onClick={() => handleRemoveAccount(account.id)}
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {showCancelConfirmation && (
        <div className="modal-overlay">
          <div className="cancel-confirmation-modal">
            <h3>Cancel Sub-Account</h3>
            <p>
              Are you sure you want to cancel the subscription for 
              <strong> {accountToCancel?.name}</strong>?
            </p>
            <p className="warning-text">
              This will immediately end your subscription. You will lose access to this account at the end of your current billing period.
            </p>
            <div className="modal-actions">
              <button 
                className="btn-cancel" 
                onClick={() => setShowCancelConfirmation(false)}
              >
                Keep Subscription
              </button>
              <button 
                className="btn-confirm-cancel" 
                onClick={confirmCancelAccount}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin /> Processing...
                  </>
                ) : (
                  'Yes, Cancel Subscription'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {showRemoveConfirmation && (
        <div className="modal-overlay">
          <div className="remove-confirmation-modal">
            <h3>Remove Inactive Account</h3>
            <p>
              Are you sure you want to remove <strong>{accountToRemove?.name}</strong> from your account list?
            </p>
            <p className="info-text">
              This account is already inactive. Removing it will only hide it from your account list and will not affect any data.
            </p>
            <div className="modal-actions">
              <button 
                className="btn-cancel" 
                onClick={() => setShowRemoveConfirmation(false)}
              >
                Cancel
              </button>
              <button 
                className="btn-confirm-remove"
                onClick={confirmRemoveAccount}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin /> Processing...
                  </>
                ) : (
                  'Remove Account'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {showRenameModal && (
        <div className="modal-overlay">
          <div className="rename-modal">
            <h3>Rename Account</h3>
            <p>
              Enter a new name for <strong>{accountToRename?.name}</strong>:
            </p>
            <div className="form-group">
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Enter new account name"
                className="rename-input"
              />
            </div>
            <div className="modal-actions">
              <button 
                className="btn-cancel" 
                onClick={() => setShowRenameModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn-confirm-rename"
                onClick={confirmRenameAccount}
                disabled={isSubmitting || !newName.trim()}
              >
                {isSubmitting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin /> Processing...
                  </>
                ) : (
                  'Rename Account'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {showResubscribeModal && (
        <div className="modal-overlay">
          <div className="resubscribe-modal">
            <h3>Resubscribe Account</h3>
            <p>
              Choose a plan to resubscribe <strong>{accountToResubscribe?.name}</strong>:
            </p>
            
            <div className="billing-toggle">
              <span className={resubscribeBillingCycle === 'monthly' ? 'active' : ''}>Monthly</span>
              <label className="switch">
                <input 
                  type="checkbox" 
                  checked={resubscribeBillingCycle === 'annual'} 
                  onChange={toggleResubscribeBillingCycle}
                />
                <span className="slider round"></span>
              </label>
              <span className={resubscribeBillingCycle === 'annual' ? 'active' : ''}>Annual (2 months free)</span>
            </div>
            
            <div className="plan-options">
              <div className="plan-option">
                <h4>Essential</h4>
                <div className="plan-price">
                  {resubscribeBillingCycle === 'annual' ? '$490/yr' : '$49/mo'}
                </div>
                <ul className="plan-features">
                  <li>Track up to 5 offers</li>
                  <li>One-Click Link Updates</li>
                  <li>Performance Dashboard</li>
                </ul>
                <button 
                  className="btn-essential"
                  onClick={handleResubscribeEssential}
                >
                  Select Essential
                </button>
              </div>
              
              <div className="plan-option highlighted">
                <div className="recommended-badge">
                  <FontAwesomeIcon icon={faStar} /> Recommended
                </div>
                <h4>Pro</h4>
                <div className="plan-price">
                  {resubscribeBillingCycle === 'annual' ? '$990/yr' : '$99/mo'}
                </div>
                <ul className="plan-features">
                  <li>Unlimited offers</li>
                  <li>One-Click Link Updates</li>
                  <li>Performance Dashboard</li>
                  <li>AI Comment Analysis</li>
                  <li>AI Video Ideas</li>
                </ul>
                <button 
                  className="btn-pro"
                  onClick={handleResubscribePro}
                >
                  Select Pro
                </button>
              </div>
            </div>
            
            <div className="modal-actions">
              <button 
                className="btn-cancel" 
                onClick={() => setShowResubscribeModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountManagement;
